import {useCallback, useEffect, useRef, useState} from 'react';

import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {settlementAccountService} from '@/services/settlementAccountService';
import type {SettlementPaymentMethodListParams} from '@/types';
import {isSettlementPaymentMethodArchived} from '@/types';
import {logRejection} from '@/utils/logRejection';

import type {SettlementAccountListProps} from './types';

const pageSize = 10;

/**
 * Settlement account list. Paginates server side and keeps the same filter and
 * pagination surface as the sibling invoice list, so `Pagination` and the
 * filter component drop in unchanged.
 *
 * Unlike the invoice list this does not route through `useFilters`: there are no
 * date filters here, and that hook's job is validating and normalising a
 * from/to pair.
 */
const useSettlementAccountList = (): SettlementAccountListProps => {
  const [list, setList] = useState<SettlementPaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const currentPageRef = useRef<number>(0);
  const [displayPage, setDisplayPage] = useState<number>(1);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] =
    useState<SettlementPaymentMethodListParams>({});
  const [filtersToApply, setFiltersToApply] =
    useState<SettlementPaymentMethodListParams>({});
  const [filterCount, setFilterCount] = useState<number>(0);

  const fetchAccounts = useCallback(
    async (params: SettlementPaymentMethodListParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const requestedPage = params.page ?? 0;
        // `includeArchived` is a UI filter, not a request param: the server has
        // no deactivated flag, so it is stripped here and applied below.
        const {includeArchived, ...requestFilters} = {
          ...appliedFilters,
          ...params,
        };
        const response =
          await settlementAccountService.getSettlementPaymentMethods({
            ...requestFilters,
            page: requestedPage,
            limit: pageSize,
          });
        currentPageRef.current = requestedPage;
        setDisplayPage(requestedPage + 1);
        setPage(requestedPage + 1);
        const rows = response.data ?? [];
        setList(
          includeArchived
            ? rows
            : rows.filter(
                (method) => !isSettlementPaymentMethodArchived(method),
              ),
        );
        setItemTotal(response.total ?? 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters],
  );

  const fetchList = useCallback(
    (params: SettlementPaymentMethodListParams) => {
      fetchAccounts(params).catch(
        logRejection('Failed to load settlement accounts'),
      );
    },
    [fetchAccounts],
  );

  const refetchCurrentPage = useCallback(async () => {
    await fetchAccounts({page: currentPageRef.current});
  }, [fetchAccounts]);

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const selectAppliedFilters = useCallback(
    (name: string, value: string | boolean | undefined) => {
      setFiltersToApply((prev) => {
        // A cleared Select hands back '', which is not a value this request
        // shape has; store undefined so the filter simply drops out.
        const normalised = value === '' ? undefined : value;
        const updated = {...prev, [name]: normalised};
        setFilterCount(
          Object.values(updated).filter(
            (val) => val !== undefined && val !== false,
          ).length,
        );
        return updated;
      });
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filtersToApply);
    setIsPageReset((prev) => !prev);
  }, [filtersToApply]);

  const clearFilters = useCallback(() => {
    setAppliedFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    error,
    isLoading,
    list,
    pageInfo: {total: itemTotal, page},
    fetchList,
    refetchCurrentPage,
    displayPage,
    isPageReset,
    filterCount,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
  };
};

export default useSettlementAccountList;
